import React, { useContext, useEffect, useRef } from "react";
import classnames from "classnames";

import ArrowRightWhite from "@vahak/core/dist/icons/arrow-right-white.svg";
import { CompanyServiceType } from "@vahak/core/dist/constants/serviceTypeId";
import { AppContext } from "@vahak/core/dist/app-context";
import { NoSuccessErrorHandler } from "@vahak/core/dist/constants/api";
import { getPriceString } from "@vahak/core-ui/dist/methods/common";
import { LORRY_CATEGORIES_ENUM } from "@vahak/core/dist/constants/lorryTypes";

import AnimateContainer from "../../login-form-new/animate-container/AnimateContainer";
import { LOGIN_PHONE_NUMBER_LENGTH } from "../../login-form-new/hooks/auth";
import { LoginFormAuthResult } from "../../login-form-new/hooks/loginFormAuth.types";
import { TrackEventHandler } from "../../../types/events";
import useCompactLoadPostingTranslation from "../compact-load-posting/hooks/useCompactLoadPostingTranslation";
import LoadSummaryChip from "../compact-load-posting/LoadSummaryChip";
import { CompactLoadPostingState } from "../compact-load-posting/types";
import { useGetLorryMarketV2 } from "../../../services/lorry/useGetLorryMarketV2/useGetLorryMarketV2";

import CompactOtpField from "./CompactOtpField";
import CompactPhoneNumberField from "./CompactPhoneNumberField";
import { COMPACT_AUTH_STEPS, CompactLoginShellFooterState } from "./compactAuth.types";
import { useCompactAuth } from "./useCompactAuth";
import Styles from "./CompactLoginFlow.module.scss";

export interface CompactLoginFlowProps {
    quoteAmount?: number;
    subtitle?: string;
    ctaLabels?: {
        sendOtp?: string;
        verify?: string;
    };
    summarySlot?: React.ReactNode;
    hideTerms?: boolean;
    phoneFieldVariant?: "default" | "bid";
    /** posting = mockup v6 body layout for compact load posting */
    visualVariant?: "default" | "posting";
    postingState?: CompactLoadPostingState;
    sourceScreen?: string;
    isInitiatedFrom?: string;
    defaultPlaceId?: string;
    defaultCompanyName?: string;
    registrationServiceType?: CompanyServiceType;
    otpSentToKey?: string;
    onComplete: (status: LoginFormAuthResult) => void | Promise<void>;
    onStepChange?: (step: COMPACT_AUTH_STEPS) => void;
    onShellBackRegister?: (handler: (() => boolean) | null) => void;
    /** Renders login CTA in ModalOrBottomSheetWrapper footer (posting flow). */
    onShellFooterChange?: (footer: CompactLoginShellFooterState | null) => void;
    trackEvent?: TrackEventHandler;
}

export function CompactLoginQuoteSummary({ amount }: { amount?: number }) {
    const { t } = useCompactLoadPostingTranslation();

    if (!amount) {
        return null;
    }

    return (
        <div className={Styles.quoteSummary}>
            <p className={Styles.quoteLabel}>{t("load-form:login.yourQuote")}</p>
            <p className={Styles.quoteValue}>{getPriceString(amount)}</p>
        </div>
    );
}

const CompactLoginFlow = ({
    quoteAmount,
    subtitle,
    ctaLabels,
    summarySlot,
    hideTerms,
    phoneFieldVariant = "default",
    visualVariant = "default",
    postingState,
    onComplete,
    onStepChange,
    onShellBackRegister,
    onShellFooterChange,
    sourceScreen,
    isInitiatedFrom,
    defaultPlaceId,
    defaultCompanyName,
    registrationServiceType,
    otpSentToKey,
    trackEvent
}: CompactLoginFlowProps) => {
    const { t } = useCompactLoadPostingTranslation();
    const { companyId, customerCompanyId } = useContext(AppContext);
    const { formik, activeStep, animation, onClickResendOtp, onClickEditNumber } = useCompactAuth({
        onComplete,
        onStepChange,
        sourceScreen,
        isInitiatedFrom,
        defaultPlaceId,
        defaultCompanyName,
        registrationServiceType
    });

    const isPosting = visualVariant === "posting";
    const sourcePlaceId = postingState?.source?.place_id;
    const destinationPlaceId = postingState?.destination?.place_id;
    const lorryTypeId = postingState?.lorryType;

    const { data: lorryMarketData } = useGetLorryMarketV2(
        {
            page: 1,
            l: 1,
            source_place_id: sourcePlaceId,
            destination_place_id: destinationPlaceId,
            c_i: customerCompanyId || companyId || undefined,
            lorry_type_id: lorryTypeId ? ([lorryTypeId] as LORRY_CATEGORIES_ENUM[]) : undefined
        },
        {
            enabled: isPosting && Boolean(sourcePlaceId && destinationPlaceId),
            refetchOnWindowFocus: false,
            retry: 0,
            ...NoSuccessErrorHandler
        }
    );

    const liveLorryCount = Number(lorryMarketData?.total_records) || 0;
    const showUrgencyStrip = isPosting && liveLorryCount > 0;

    useEffect(() => {
        if (!onShellBackRegister) {
            return;
        }

        onShellBackRegister(() => {
            if (activeStep === COMPACT_AUTH_STEPS.OTP) {
                onClickEditNumber();
                return true;
            }
            return false;
        });

        return () => onShellBackRegister(null);
    }, [activeStep, onClickEditNumber, onShellBackRegister]);

    /**
     * Send the OTP as soon as the number is complete — the field caps at 10 digits, so the
     * CTA adds a tap without adding a decision. Keyed on the number itself: a rejected send
     * is not retried on every render, and coming back via "change number" waits for an edit.
     */
    const autoSubmittedPhoneRef = useRef<string | null>(null);

    useEffect(() => {
        const phoneNumber = String(formik.values.phoneNumber ?? "");

        if (
            activeStep !== COMPACT_AUTH_STEPS.PHONE ||
            phoneNumber.length !== LOGIN_PHONE_NUMBER_LENGTH ||
            !formik.isValid ||
            formik.isSubmitting ||
            autoSubmittedPhoneRef.current === phoneNumber
        ) {
            return;
        }

        autoSubmittedPhoneRef.current = phoneNumber;
        void formik.submitForm();
    }, [activeStep, formik]);

    const resolvedSummary = summarySlot ?? (quoteAmount ? <CompactLoginQuoteSummary amount={quoteAmount} /> : null);
    const showTerms =
        !hideTerms &&
        (isPosting
            ? [COMPACT_AUTH_STEPS.PHONE, COMPACT_AUTH_STEPS.OTP].includes(activeStep)
            : activeStep === COMPACT_AUTH_STEPS.PHONE);

    const urgencyStrip = showUrgencyStrip ? (
        <div className={Styles.urgencyStrip}>
            <span className={Styles.liveDot} aria-hidden />
            {t("load-form:login.urgencyStrip", { count: liveLorryCount })}
        </div>
    ) : null;

    const getCtaLabel = (): string => {
        if (activeStep === COMPACT_AUTH_STEPS.PHONE) {
            return ctaLabels?.sendOtp ?? t("load-form:login.sendOtp") ?? "";
        }
        if (activeStep === COMPACT_AUTH_STEPS.OTP) {
            return ctaLabels?.verify ?? t("load-form:login.verifyAndContinue") ?? "";
        }
        return t("load-form:login.continue") ?? "";
    };

    const renderStep = () => {
        switch (activeStep) {
            case COMPACT_AUTH_STEPS.PHONE:
                return (
                    <>
                        {!isPosting && subtitle ? <p className={Styles.subtitle}>{subtitle}</p> : null}
                        {urgencyStrip}
                        <CompactPhoneNumberField
                            formik={formik}
                            variant={phoneFieldVariant}
                            layout={isPosting ? "inline" : "default"}
                            trackEvent={trackEvent}
                        />
                        {!isPosting && resolvedSummary ? (
                            <div className={Styles.summarySlot}>{resolvedSummary}</div>
                        ) : null}
                    </>
                );
            case COMPACT_AUTH_STEPS.OTP:
                return (
                    <>
                        {urgencyStrip}
                        <CompactOtpField
                            formik={formik}
                            onClickResendOtp={onClickResendOtp}
                            onClickEditNumber={onClickEditNumber}
                            otpSentToKey={otpSentToKey}
                            compactBoxes={isPosting}
                            trackEvent={trackEvent}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const showCta = [COMPACT_AUTH_STEPS.PHONE, COMPACT_AUTH_STEPS.OTP].includes(activeStep);
    const useShellFooter = isPosting && Boolean(onShellFooterChange);

    useEffect(() => {
        if (!useShellFooter || !onShellFooterChange) {
            return;
        }

        if (!showCta) {
            onShellFooterChange(null);
            return;
        }

        onShellFooterChange({
            ctaLabel: getCtaLabel(),
            ctaDisabled: !formik.isValid || formik.isSubmitting,
            onCtaClick: () => {
                formik.submitForm();
            }
        });

        return () => onShellFooterChange(null);
    }, [
        activeStep,
        ctaLabels?.sendOtp,
        ctaLabels?.verify,
        formik.isSubmitting,
        formik.isValid,
        onShellFooterChange,
        showCta,
        t,
        useShellFooter
    ]);

    const terms = showTerms ? (
        <p className={Styles.terms}>
            {t("load-form:login.termsPrefix")}{" "}
            <a href="/terms-and-conditions" className={Styles.termsLink} target="_blank" rel="noopener noreferrer">
                {t("load-form:login.termsLink")}
            </a>{" "}
            {t("load-form:login.termsJoiner")}{" "}
            <a href="/privacy-policy" className={Styles.termsLink} target="_blank" rel="noopener noreferrer">
                {t("load-form:login.privacyLink")}
            </a>{" "}
            {t("load-form:login.termsSuffix")}
        </p>
    ) : null;

    const cta = showCta ? (
        <button type="submit" className={Styles.cta} disabled={!formik.isValid || formik.isSubmitting}>
            <span>{getCtaLabel()}</span>
            <ArrowRightWhite width={16} height={16} aria-hidden />
        </button>
    ) : null;

    return (
        <div
            className={classnames(
                Styles.flow,
                isPosting && Styles.flowPosting,
                useShellFooter && Styles.flowPostingShellFooter
            )}
        >
            {isPosting && postingState ? (
                <LoadSummaryChip state={postingState} variant="bodyStacked" showLoadingTime includeVehicleDetails />
            ) : null}
            <form
                className={classnames(isPosting && Styles.postingForm, useShellFooter && Styles.postingFormShellFooter)}
                onSubmit={(e) => {
                    e.preventDefault();
                    formik.submitForm();
                }}
            >
                <div
                    className={classnames(
                        isPosting && Styles.postingBody,
                        useShellFooter && Styles.postingBodyShellFooter
                    )}
                >
                    <AnimateContainer animation={animation}>{renderStep()}</AnimateContainer>
                    {isPosting ? terms : null}
                    {!isPosting ? (
                        <>
                            {cta}
                            {terms}
                        </>
                    ) : null}
                </div>
                {isPosting && !useShellFooter ? <div className={Styles.postingFooter}>{cta}</div> : null}
            </form>
        </div>
    );
};

export default CompactLoginFlow;
